import type { State } from '@src/redux/configureStore'
import { Identities as ActionTypes } from '@src/redux/actionTypes'
import {
  defaultState,
  ObjectItem,
  IdentitySummaryItem,
  IdentitiesState
} from '@src/redux/reducers/identities/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import {
  GraphQueryMgr,
  IdentityInfo,
  IdentityQueryReq,
  IdentityServiceQueryReq,
  DataType,
  IdentityTileType
} from '@src/endpoints/services/graphQueryMgr.service'
import { Usagemanager } from '@src/endpoints/services/usagemanager.service'
import {
  formatSearchResultByField,
  InfoDetailsResult,
  parseInfoDetails,
  ResourceInfo
} from '@src/utils/resource.utils'
import { parseGraph, mapDataToGraph } from '@src/utils/graph.helpers'
import {
  parseIndentityDetailsStats,
  parseIndentityDetailsTileData
} from './helpers/identity.details'
import type { TileDataType } from './helpers/identity.details'
import type { IdentityTiles } from '@src/modules/identities/configurations/tiles.configurations'
import {
  getIdentitiesTotals,
  formatIdentitySummaryInfo,
  formatUserLocations
} from '@src/utils/identity.utils'
import type { SearchResult } from '@src/redux/reducers/autocomplete.query/default.state'
import type { Graph } from '@src/components/vis.js.network.vis/models'
import { keys, pickBy, includes } from 'lodash'
import { defaultHandleStatus, paramsWithFilter } from '@src/endpoints/helpers'
import { getInfo } from '@src/redux/reducers/objects/helpers/object.info.mapper'
import type { AuthState } from '@src/redux/reducers/auth'
import { parseScopeList } from '@src/redux/reducers/identities/helpers'
import {
  createScopeInstance,
  getScopeDisplayName
} from '@src/utils/scope.utils'

// Action Creators
export type ActionTypes =
  | getStatSuccessAction
  | cleanerStatAction
  | requestPendingAction
  | requestPendingGraphAction
  | serverErrorsAction
  | searchResultsSuccessAction
  | cleaningAction
  | getOverviewStatsDataAction
  | setSelectedViewAction
  | detailsCleanerAction
  | disposeDetailsAction
  | changeSummaryStateAction
  | errorsUserLocationsAction
  | getDetailsPageTableDataAction
  | getDetailsPageTileCountsAction
  | getDetailsPageGraphDataAction

const getStatSuccess = ReduxHelpers.Common.Actions.setToState(
  ActionTypes.GET_STAT_SUCCESS,
  defaultState
)
type getStatSuccessAction = ReturnType<typeof getStatSuccess>

export const changeSummaryState = ReduxHelpers.Common.Actions.changeState(
  ActionTypes.CHANGE_STATE,
  defaultState
)
type changeSummaryStateAction = ReturnType<typeof changeSummaryState>

export const cleanerStat = ReduxHelpers.Common.Actions.cleanerState(
  ActionTypes.CLEANER_STAT,
  defaultState
)
type cleanerStatAction = ReturnType<typeof cleanerStat>

export const detailsCleaner = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.DETAILS_CLEANER)
type detailsCleanerAction = ReturnType<typeof detailsCleaner>

const errorsUserLocations = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_USER_LOCATION_SERVER_ERRORS,
    {
      errors,
      isPending: false
    }
  )
type errorsUserLocationsAction = ReturnType<typeof errorsUserLocations>

const serverErrors = (errors: any, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors,
    isPending
  })
type serverErrorsAction = ReturnType<typeof serverErrors>

const searchResultsSuccess = (graph: Graph, objectList: ObjectItem[]) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SEARCH_RESULTS_SUCCESS, {
    graph,
    objectList,
    isPending: false
  })
type searchResultsSuccessAction = ReturnType<typeof searchResultsSuccess>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export const cleaning = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.CLEANING)
type cleaningAction = ReturnType<typeof cleaning>

export const disposeDetails = () =>
  ReduxHelpers.Actions.createAction(ActionTypes.DISPOSE_DETAILS)
type disposeDetailsAction = ReturnType<typeof disposeDetails>

export const requestPendingGraph = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING_GRAPH, {
    isPending
  })
type requestPendingGraphAction = ReturnType<typeof requestPendingGraph>

export const getOverviewStatsData = (
  data: {
    list?: IdentitySummaryItem[]
    totals?: ReturnType<typeof getIdentitiesTotals>
  },
  type?: ResourceTypeEnum
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_IDENTITIES_OVERVIEW_STATS_DATA,
    {
      type,
      data,
      isPending: false
    }
  )
type getOverviewStatsDataAction = ReturnType<typeof getOverviewStatsData>

export const setSelectedView = (tile: IdentityTiles, counter: string) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_SELECTED_TILE, {
    tile,
    counter
  })
type setSelectedViewAction = ReturnType<typeof setSelectedView>

export const getDetailsPageTableData = (
  data: TileDataType,
  auth: AuthState,
  tile: IdentityTileType
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_IDENTITY_DETAILS_TABLE_DATA,
    {
      data,
      auth,
      tile,
      isPending: false
    }
  )
type getDetailsPageTableDataAction = ReturnType<typeof getDetailsPageTableData>

export const getDetailsPageGraphData = (graph: Graph) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_IDENTITY_DETAILS_GRAPH_DATA,
    {
      graph,
      isPendingGraph: false
    }
  )
type getDetailsPageGraphDataAction = ReturnType<typeof getDetailsPageGraphData>

export const getDetailsPageTileCounts = (
  data: IdentitiesState,
  auth: AuthState
) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_IDENTITY_DETAILS_COUNTS_DATA,
    {
      data,
      auth,
      isPending: false
    }
  )
type getDetailsPageTileCountsAction = ReturnType<
  typeof getDetailsPageTileCounts
>

export function getTotals() {
  return (dispatch: any, getState: () => State) => {
    const { filter, identities } = getState()
    const { selectedIdentityType } = identities

    dispatch(
      changeSummaryState({
        overviewList: defaultState.overviewList,
        isPending: true,
        totals: defaultState.totals
      })
    )
    GraphQueryMgr.IdentityQuery({ params: paramsWithFilter({}, filter) })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { identity_summary_info = [], identity_count = {} } = res
        const totals = getIdentitiesTotals(identity_count)
        const totalNames: ResourceTypeEnum[] = (keys(
          pickBy(totals, value => Number(value) > 0)
        ) || []) as ResourceTypeEnum[]
        let firstType: ResourceTypeEnum = ResourceTypeEnum[totalNames[0]]
        firstType =
          selectedIdentityType && includes(totalNames, selectedIdentityType)
            ? selectedIdentityType
            : firstType
        const hasUsersType = includes(totalNames, ResourceTypeEnum.USER)

        if (firstType) {
          let list: IdentitySummaryItem[] = defaultState.overviewList
          if (hasUsersType) {
            const { auth } = getState()
            const { tenant } = auth
            const { account_info = [] } = tenant || {}
            list = identity_summary_info.map(item =>
              formatIdentitySummaryInfo(item, account_info)
            )
          }

          dispatch(
            getOverviewStatsData(
              {
                totals,
                list
              },
              firstType
            )
          )
          if (!hasUsersType) {
            dispatch(getOverviewData(firstType))
          }
        } else {
          dispatch(
            getOverviewStatsData(
              {
                list: [],
                totals
              },
              null
            )
          )
        }
      })
      .catch((res: Response & { name?: string }) => {
        const isAbort = res?.name === 'AbortError'
        if (isAbort) {
          const { identities } = getState()
          const { isPending } = identities || {}
          dispatch(serverErrors(res?.statusText, isPending))
        } else {
          dispatch(serverErrors(res?.statusText))
        }
      })
  }
}

export function getOverviewData(type?: ResourceTypeEnum, useFilter = true) {
  return (dispatch: any, getState: () => State) => {
    const params: IdentityQueryReq = {}
    const { filter, auth } = getState()
    const { tenant } = auth
    const { account_info = [] } = tenant || {}
    if (type) {
      params.type = type
    }
    dispatch(
      changeSummaryState({
        isPending: true,
        overviewList: defaultState.overviewList,
        selectedIdentityType: type
      })
    )
    GraphQueryMgr.IdentityQuery({
      params: useFilter ? paramsWithFilter(params, filter) : params
    })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { identity_summary_info = [], identity_count = {} } = res
        const totals = getIdentitiesTotals(identity_count)
        const list: IdentitySummaryItem[] = identity_summary_info.map(item =>
          formatIdentitySummaryInfo(item, account_info)
        )

        dispatch(
          getOverviewStatsData(
            {
              list,
              totals
            },
            type
          )
        )
      })
      .catch((res: Response & { name?: string }) => {
        const isAbort = res?.name === 'AbortError'
        if (isAbort) {
          const { identities } = getState()
          const { isPending } = identities || {}
          dispatch(serverErrors(res?.statusText, isPending))
        } else {
          dispatch(serverErrors(res?.statusText))
        }
      })
  }
}

export function cleaner() {
  return (dispatch: any, getState: () => State) => {
    dispatch(disposeDetails())
  }
}

export function getDetails(
  identityName: string,
  identityType: ResourceTypeEnum,
  accountId: string,
  tile = IdentityTileType.TILE_OBJECT
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(getTileCounts(identityName, identityType, accountId))
    dispatch(getGraphData(identityName, identityType, accountId))
    dispatch(getTableData(identityName, identityType, accountId, tile, true))
  }
}

export function getTileCounts(
  uri: string,
  type: ResourceTypeEnum,
  account_id: string
) {
  return (dispatch: any, getState: () => State) => {
    const params: IdentityServiceQueryReq = {
      uri,
      type,
      account_id,
      data_type: DataType.INFO
    }
    dispatch(detailsCleaner())
    dispatch(requestPending(true))
    GraphQueryMgr.IdentityServiceQuery({ params })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { identity_info = {} as IdentityInfo } = res
        const { auth } = getState()
        const { valueMap = 'null' } = identity_info

        const parsedInfoDetails: ResourceInfo = getInfo(valueMap)
        const infoDetailsResult: InfoDetailsResult =
          parseInfoDetails(parsedInfoDetails)

        const identityData = parseIndentityDetailsStats(
          identity_info,
          infoDetailsResult,
          auth
        )

        dispatch(getDetailsPageTileCounts(identityData, auth))
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

export function getTableData(
  uri: string,
  type: ResourceTypeEnum,
  account_id: string,
  tile: IdentityTileType,
  needGetUserLocations = false
) {
  return (dispatch: any, getState: () => State) => {
    const params: IdentityServiceQueryReq = {
      uri,
      type,
      account_id,
      data_type: DataType.TABLE_DATA,
      tile
    }
    dispatch(requestPending(true))
    GraphQueryMgr.IdentityServiceQuery({ params })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { identity_info = {} as IdentityInfo } = res
        const { auth } = getState()
        const { tenant } = auth
        const { account_info = [] } = tenant || {}
        const { result_table = [] } = identity_info

        let formattedTable: SearchResult[] = result_table || []
        formattedTable = formatSearchResultByField(
          formattedTable,
          'resource_uri',
          account_info
        )
        const identityData = parseIndentityDetailsTileData(identity_info)
        if (tile === IdentityTileType.TILE_OBJECT) {
          dispatch(
            getDetailsPageTableData({ objectList: formattedTable }, auth, tile)
          )
        } else if (tile === IdentityTileType.TILE_SCOPE) {
          const scopeList = parseScopeList(identityData, auth)
          dispatch(
            getDetailsPageTableData({ ...identityData, scopeList }, auth, tile)
          )
        } else {
          dispatch(getDetailsPageTableData(identityData, auth, tile))
        }
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

export function getGraphData(
  uri: string,
  type: ResourceTypeEnum,
  account_id: string
) {
  return (dispatch: any, getState: () => State) => {
    const params: IdentityServiceQueryReq = {
      uri,
      type,
      account_id,
      data_type: DataType.ROLL_UP_GRAPH
    }
    const { auth, shell } = getState()
    const { resourceTypes } = shell

    dispatch(requestPendingGraph(true))
    GraphQueryMgr.IdentityServiceQuery({ params })
      .then(res => defaultHandleStatus(res, serverErrors, true, dispatch))
      .then(res => {
        const { identity_info = {} as IdentityInfo } = res
        const { result_graph, roll_up_graph } = identity_info
        const graphParsed = result_graph
          ? parseGraph(result_graph)
          : mapDataToGraph({
              data: roll_up_graph,
              resourceTypes,
              options: {
                mainIdKey: 'identity_uri',
                mainNameKey: 'identity',
                typeKey: 'resource_type',
                countKey: 'resource_count'
              }
            })
        const graph = {
          ...graphParsed,
          nodes: ((graphParsed || {})?.nodes || []).map(item => ({
            ...item,
            name: getScopeDisplayName(
              createScopeInstance(item?.name).parse(auth)?.value
            )
          }))
        }
        dispatch(getDetailsPageGraphData(graph))
      })
      .catch((res: Response) => dispatch(serverErrors(res?.statusText)))
  }
}

interface GetDetailsForTileOptions {
  selectedTile?: IdentityTiles
  selectedCounter?: string
}

export function getDetailsForTile({
  selectedTile,
  selectedCounter
}: GetDetailsForTileOptions) {
  return (dispatch: any, getState: () => State) => {
    dispatch(setSelectedView(selectedTile, selectedCounter))
  }
}

interface GetUserLocationsOptions {
  userName: string
  accountId: string
}

export function getUserLocations({
  userName,
  accountId
}: GetUserLocationsOptions) {
  return (dispatch: any, getState: () => State) => {
    Usagemanager.GetUserLocationData({
      makeRequestOptions: {
        handleResponseErrors: res => {}
      },
      params: {
        username: userName,
        account_id: accountId
      }
    })
      .then(res =>
        defaultHandleStatus(res, errorsUserLocations, false, dispatch)
      )
      .then(res => {
        const userLocations =
          res.userlocationmap &&
          formatUserLocations(res.userlocationmap[userName])
        dispatch(getStatSuccess('userLocations', userLocations))
      })
      .catch((res: Response) => dispatch(errorsUserLocations(res?.statusText)))
  }
}
